import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Eye, EyeOff, PenLine } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  isAutoField,
  cleanDigitsValue,
  digitCellsOf,
  digitsComplete,
  fieldEnabled,
  fieldRequired,
  formatDigits,
  groupMembersOrdered,
} from './documentHelpers';

// Guided signing (Nic 2026-10-15: on a phone the PDF's tiny boxes made
// signing feel broken). The signer walks the document one field at a time in
// a card with a big readable input: the page scrolls to and rings the field,
// "Field 3 of 9", Back / Next. Linked one-character cells (an SSN or EIN
// typed into 9 boxes) are ONE step with one input; the digits fill the boxes,
// so the signed PDF is unchanged.

const isCheckable = (p) => p.type === 'checkbox' || p.type === 'radio';
const isCombCell = (p) => (p.type === 'text' || p.type === 'number') && p.maxLength === 1;

// Plain names for unnamed fields. A template field with a label wins.
const TYPE_NAME = {
  text: 'Text',
  long_text: 'Text',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  radio: 'Choice',
  signature: 'Your signature',
  digits: 'Number',
};

// A typed field is answered when it has a value; digit boxes only when every
// box is filled.
const typedComplete = (p, v) =>
  p.type === 'digits'
    ? digitsComplete(p, v)
    : v !== undefined && v !== null && String(v).trim() !== '';

// A group's name only when it reads as a name, not the builder's default
// ("7 Checkboxes").
const groupNameOf = (p) => {
  const n = String(p.groupName || '').trim();
  return n && !/^\d+\s+(checkboxes|radios|fields)$/i.test(n) ? n : null;
};

// What a digit group is, from its name: SSN and EIN get their usual dashes
// and a spelled-out name.
const combKind = (name) => {
  const n = String(name || '').toLowerCase();
  if (/\bssn\b|social/.test(n)) return 'ssn';
  if (/\bein\b|employer/.test(n)) return 'ein';
  return null;
};
const COMB_LABEL = { ssn: 'Social Security number', ein: 'Employer Identification Number (EIN)' };
export const formatComb = (digits, kind) => {
  const d = String(digits || '');
  if (kind === 'ssn') return [d.slice(0, 3), d.slice(3, 5), d.slice(5, 9)].filter(Boolean).join('-');
  if (kind === 'ein') return [d.slice(0, 2), d.slice(2, 9)].filter(Boolean).join('-');
  return d;
};

// Masked numbers (SSN, EIN) without a password box (Nic 2026-10-24): a
// type="password" input made iPhone Keychain and Chrome offer to save the SSN
// as a password. A text box drawn as dots masks it instead; only a browser
// that can't draw dots falls back to a password box.
const CAN_DOT_TEXT = (() => {
  try {
    return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');
  } catch (_) {
    return false;
  }
})();
export const maskProps = (masked) => {
  if (!masked) return { type: 'text' };
  return CAN_DOT_TEXT ? { type: 'text', style: { WebkitTextSecurity: 'disc' } } : { type: 'password' };
};

// Reading order on the page: page, then top to bottom, then left to right
// (a small tolerance keeps one printed line together).
export const readingOrder = (a, b) =>
  (a.page || 1) - (b.page || 1) ||
  (Math.abs(a.y - b.y) > 0.008 ? a.y - b.y : a.x - b.x);

/**
 * The steps a signer walks for one document, in reading order. Each step:
 *   { key, kind: 'input' | 'comb' | 'choice' | 'check' | 'signature',
 *     fields: [placements], label, required, page, anchorId }
 * Auto fields (dates, static text, prepared regions) and fields switched
 * off by a condition are left out.
 */
export const buildGuideSteps = (placements, values) => {
  const list = (placements || []).filter(
    (p) => !isAutoField(p) && fieldEnabled(p, values),
  );
  const seenGroups = new Set();
  const steps = [];
  [...list].sort(readingOrder).forEach((p) => {
    if (p.group && seenGroups.has(p.group)) return;
    const members = p.group
      ? groupMembersOrdered(list, p.group).filter((q) => fieldEnabled(q, values))
      : [p];
    if (p.group) seenGroups.add(p.group);
    const gName = groupNameOf(p);
    if (p.group && members.length > 1 && members.every(isCombCell)) {
      const kind = combKind(p.groupName);
      steps.push({
        key: `g:${p.group}`,
        kind: 'comb',
        combKind: kind,
        fields: members,
        label: COMB_LABEL[kind] || gName || 'Number',
        required: members.some((m) => fieldRequired(m, values)),
        sensitive: members.some((m) => m.sensitive),
        numeric: members.every((m) => m.type === 'number'),
        page: p.page || 1,
        anchorId: members[0].fieldId,
      });
      return;
    }
    if (p.group && members.length > 1 && members.every(isCheckable)) {
      steps.push({
        key: `g:${p.group}`,
        kind: 'choice',
        single: members.some((m) => m.groupSingle) || members.every((m) => m.type === 'radio'),
        fields: members,
        label: gName || 'Choose one',
        required:
          members.some((m) => m.groupRequired) || members.some((m) => fieldRequired(m, values)),
        page: p.page || 1,
        anchorId: members[0].fieldId,
      });
      return;
    }
    // A group of other fields is walked one field at a time. A "require one
    // of these" group (typed members, e.g. SSN or EIN) marks each member as
    // one of these, required until any member is filled in full.
    const oneOf = p.group && members.some((m) => m.groupRequired && !isCheckable(m));
    const oneOfDone = oneOf && members.some((m) => typedComplete(m, values[m.fieldId]));
    members.forEach((m) => {
      steps.push({
        key: m.fieldId,
        kind:
          m.type === 'signature'
            ? 'signature'
            : m.type === 'digits'
              ? 'digits'
              : isCheckable(m)
                ? 'check'
                : 'input',
        fields: [m],
        label: (m.label && String(m.label).trim()) || gName || TYPE_NAME[m.type] || 'Field',
        required:
          m.type === 'signature'
            ? true
            : fieldRequired(m, values) ||
              Boolean(oneOf && !oneOfDone) ||
              // Half-filled digit boxes: finish or clear (QA 2026-10-15).
              (m.type === 'digits' &&
                cleanDigitsValue(m, values[m.fieldId]) !== '' &&
                !digitsComplete(m, values[m.fieldId])),
        oneOf: oneOf ? gName || 'One of these' : null,
        page: m.page || 1,
        anchorId: m.fieldId,
      });
    });
  });
  return steps;
};

// Is the step answered?
export const stepFilled = (step, values) => {
  const v = (p) => values[p.fieldId];
  const has = (x) => x !== undefined && x !== null && String(x).trim() !== '';
  if (step.kind === 'comb') return step.fields.every((p) => has(v(p)));
  if (step.kind === 'choice') return step.fields.some((p) => Boolean(v(p)));
  if (step.kind === 'check') return Boolean(v(step.fields[0]));
  if (step.kind === 'digits') return digitsComplete(step.fields[0], v(step.fields[0]));
  return has(v(step.fields[0]));
};

// The first step still owed, preferring required ones; null when all done.
export const nextOpenStep = (steps, values, from = -1) => {
  const open = (s) => !stepFilled(s, values);
  for (let i = from + 1; i < steps.length; i += 1) {
    if (steps[i].required && open(steps[i])) return i;
  }
  for (let i = 0; i <= from && i < steps.length; i += 1) {
    if (steps[i].required && open(steps[i])) return i;
  }
  const anyOpen = steps.findIndex(open);
  return anyOpen >= 0 ? anyOpen : null;
};

const bigInput =
  'w-full rounded-xl border border-input bg-white px-3.5 py-3 text-base text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30';

/**
 * The card at the bottom of the screen for one step. `values` + `setValue`
 * are the signer's per-document values; `fitsBox(p, v)` says whether a typed
 * value still fits a limited box (the page's own rule).
 */
export const GuideCard = ({
  step,
  index,
  total,
  values,
  setValue,
  onCheckable,
  onOpenSignature,
  fitsBox,
  onBack,
  onNext,
  onClose,
  isLast,
}) => {
  const inputRef = useRef(null);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    setReveal(false);
    // Focus the input for typing steps (brings up the right keyboard).
    if (['input', 'comb', 'digits'].includes(step.kind)) {
      const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step.key, step.kind]);

  const p = step.fields[0];
  const filled = stepFilled(step, values);
  const enterNext = (e) => {
    if (e.key === 'Enter' && step.kind !== 'input') onNext();
    if (e.key === 'Enter' && step.kind === 'input' && p.type !== 'long_text') onNext();
  };

  let body = null;
  if (step.kind === 'comb') {
    const digits = step.fields.map((f) => values[f.fieldId] || '').join('');
    const shown = formatComb(digits, step.combKind);
    body = (
      <div className="relative">
        <input
          ref={inputRef}
          {...maskProps(step.sensitive && !reveal)}
          inputMode={step.numeric ? 'numeric' : 'text'}
          autoComplete="off"
          value={step.sensitive && !reveal ? digits : shown}
          onChange={(e) => {
            const raw = step.numeric ? e.target.value.replace(/\D/g, '') : e.target.value.replace(/[\s-]/g, '');
            const chars = raw.slice(0, step.fields.length).split('');
            step.fields.forEach((f, i) => setValue(f.fieldId, chars[i] || ''));
          }}
          onKeyDown={enterNext}
          placeholder={formatComb('0'.repeat(step.fields.length), step.combKind).replace(/0/g, '•')}
          className={cn(bigInput, 'pr-12 font-mono tracking-[0.2em]')}
          aria-label={step.label}
        />
        {step.sensitive ? (
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            aria-label={reveal ? 'Hide' : 'Show'}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100"
          >
            {reveal ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        ) : null}
        <div className="mt-1.5 text-xs text-muted-foreground">
          {digits.length} of {step.fields.length} digits
        </div>
      </div>
    );
  } else if (step.kind === 'digits') {
    const cells = digitCellsOf(p);
    const raw = cleanDigitsValue(p, values[p.fieldId]);
    const masked = p.sensitive && !reveal;
    body = (
      <div className="relative">
        <input
          ref={inputRef}
          {...maskProps(masked)}
          inputMode={p.allowLetters ? 'text' : 'numeric'}
          autoComplete="off"
          value={masked ? raw : formatDigits(p, raw)}
          onChange={(e) => setValue(p.fieldId, cleanDigitsValue(p, e.target.value))}
          onKeyDown={enterNext}
          placeholder={formatDigits(p, '0'.repeat(cells)).replace(/0/g, '•')}
          className={cn(bigInput, 'pr-12 font-mono tracking-[0.2em]')}
          aria-label={step.label}
        />
        {p.sensitive ? (
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            aria-label={reveal ? 'Hide' : 'Show'}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100"
          >
            {reveal ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        ) : null}
        <div className="mt-1.5 text-xs text-muted-foreground">
          {raw.length} of {cells} {p.allowLetters ? 'characters' : 'digits'}
        </div>
      </div>
    );
  } else if (step.kind === 'choice') {
    // Tight rows (Nic 2026-10-15: a 7-option group filled the screen); still
    // a comfortable tap height.
    body = (
      <div role={step.single ? 'radiogroup' : 'group'} aria-label={step.label} className="grid gap-1">
        {step.fields.map((f, i) => {
          const on = Boolean(values[f.fieldId]);
          return (
            <button
              key={f.fieldId}
              type="button"
              role={step.single ? 'radio' : 'checkbox'}
              aria-checked={on}
              onClick={() => onCheckable(f, !on)}
              className={cn(
                'flex min-h-[40px] w-full items-center gap-2.5 rounded-lg border px-3 py-1.5 text-left text-sm leading-snug outline-none transition-colors',
                on
                  ? 'border-primary bg-primary/5 font-semibold text-foreground'
                  : 'border-input bg-white text-neutral-700 hover:border-neutral-400',
              )}
            >
              <span
                className={cn(
                  'flex h-[18px] w-[18px] shrink-0 items-center justify-center border-2',
                  step.single ? 'rounded-full' : 'rounded-md',
                  on ? 'border-primary bg-primary text-white' : 'border-neutral-300',
                )}
              >
                {on ? <Check className="h-3 w-3" /> : null}
              </span>
              {(f.label && String(f.label).trim()) || `Option ${i + 1}`}
            </button>
          );
        })}
      </div>
    );
  } else if (step.kind === 'check') {
    const on = Boolean(values[p.fieldId]);
    body = (
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        onClick={() => onCheckable(p, !on)}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left text-base outline-none',
          on ? 'border-primary bg-primary/5 font-semibold' : 'border-input bg-white text-neutral-700',
        )}
      >
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2',
            on ? 'border-primary bg-primary text-white' : 'border-neutral-300',
          )}
        >
          {on ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        {on ? 'Checked' : 'Check this box'}
      </button>
    );
  } else if (step.kind === 'signature') {
    const sig = values[p.fieldId];
    body = (
      <button
        type="button"
        onClick={() => onOpenSignature(p.fieldId)}
        className={cn(
          'flex h-24 w-full items-center justify-center rounded-xl border-2 border-dashed outline-none',
          sig ? 'border-emerald-300 bg-white' : 'border-amber-300 bg-amber-50 text-amber-800',
        )}
      >
        {sig ? (
          <img src={sig} alt="Your signature" className="max-h-20 max-w-full object-contain" />
        ) : (
          <span className="flex items-center gap-2 text-base font-semibold">
            <PenLine className="h-5 w-5" /> Tap to sign
          </span>
        )}
      </button>
    );
  } else {
    const value = values[p.fieldId] || '';
    const change = (raw) => {
      let v = raw;
      if (p.type === 'number') {
        v = v.replace(/[^\d.]/g, '');
        const dot = v.indexOf('.');
        if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
      }
      if (p.maxLength && v.length > p.maxLength) v = v.slice(0, p.maxLength);
      if (fitsBox && !fitsBox(p, v) && v.length > value.length) return;
      setValue(p.fieldId, v);
    };
    body =
      p.type === 'long_text' ? (
        <textarea
          ref={inputRef}
          rows={4}
          value={value}
          onChange={(e) => change(e.target.value)}
          className={cn(bigInput, 'resize-none')}
          aria-label={step.label}
        />
      ) : (
        <input
          ref={inputRef}
          type={p.type === 'date' ? 'date' : 'text'}
          inputMode={p.type === 'number' ? 'decimal' : undefined}
          value={value}
          onChange={(e) => change(e.target.value)}
          onKeyDown={enterNext}
          className={bigInput}
          aria-label={step.label}
        />
      );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-3 pt-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold tabular-nums text-muted-foreground">
          Field {index + 1} of {total}
        </span>
        <span
          className={cn(
            'rounded-full px-2 py-px text-[11px] font-semibold',
            filled
              ? 'bg-emerald-100 text-emerald-800'
              : step.required
                ? 'bg-amber-100 text-amber-800'
                : 'bg-blue-100 text-blue-800',
          )}
        >
          {filled ? 'Done' : step.oneOf && step.required ? 'One of these' : step.required ? 'Required' : 'Optional'}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>
      <div className="mb-2 text-base font-bold text-foreground">{step.label}</div>
      {step.oneOf && step.required && !filled ? (
        <p className="-mt-1 mb-2 text-xs text-amber-700">
          {step.oneOf}: fill in one of these. Skip this one if you are filling in the other.
        </p>
      ) : null}
      {body}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={index === 0}
          className="inline-flex items-center justify-center gap-1 rounded-xl border border-input bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-accent disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          {isLast ? 'Done' : filled || !step.required ? 'Next' : 'Skip for now'}
          {isLast ? <Check className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};
